import { BehaviorSubject } from 'rxjs';

import { Injectable } from '@angular/core';
import { AppVersion } from '@awesome-cordova-plugins/app-version/ngx';
import { environment } from '@environments/environment';
import { randCity, randEmail, randFirstName, randFullName, randLastName, randNumber, randStreetAddress, randZipCode } from '@ngneat/falso';
import * as Sentry from '@sentry/angular';
import { DefaultObject } from '@shared/interfaces';

import { CacheService } from './cache.service';

const fns = {
	randEmail,
	randUsername: () => 'jg+son@jtmg.io',
	randPassword: () => 'auk.HXA4wyf5qhf4vpg',
	randCode: () => '00102',
	randFirstName,
	randLastName,
	randNumber,
	randPastDate: () => `11/20/1981`,
	randPhoneNumber: () => `(850) 879-1379`,
	randStateAbbr: () => 'GA',
	randStreetAddress,
	randZipCode,
	randCity,
	randFullName,
	randMonth: () => `0${new Date().getMonth() + 1}`.slice(-2),
	randCreditCardNumber: () => 4012000098765439,
	randCreditCardCVV: () => 999,
	randLicenseNumber: () => `GA${randNumber({ min: 9999999, max: 999999999 })}`,
};
type env =
	| 'production'
	| 'isLocalTesting'
	| 'adminUrl'
	| 'authEndPoint'
	| 'paymentsEndPoint'
	| 'adminEndPoint'
	| 'loggingEndPoint'
	| 'storageEndPoint'
	| 'trackingEndPoint'
	| 'pricingEndPoint'
	| 'featuresEndPoint'
	| 'companyEndPoint'
	| 'carrierEndPoint'
	| 'productsEndPoint'
	| 'registrationEndPoint'
	| 'reservationsEndPoint'
	| 'invoicesEndPoint'
	| 'insuranceEndPoint'
	| 'customersEndPoint'
	| 'employeesEndPoint'
	| 'insuranceFacilityEndpoint'
	| 'insuranceQuotesEndPoint'
	| 'insuranceQuotesConfigEndPoint'
	| 'insurancePaymentsEndPoint'
	| 'rentalsApiEndPoint'
	| 'env'
	| 'apiKey'
	| 'sentryDSN'
	| 'manifestUrl'
	| 'rxjs_delay';

@Injectable({
	providedIn: 'root',
})
export class ConfigService {
	public config: DefaultObject;
	private isDevLogin = null;
	private isSentryInitialized: Boolean = false;
	private debugEmails: string[] = ['jg+jonah@jtmg.io', 'jg@jtmg.io', 'josh@vquiprentals.com'];
	private debugIds: string[] = ['00102'];
	private useFill: boolean = false;

	public config$ = new BehaviorSubject<DefaultObject>(null);

	constructor(
		private cacheService: CacheService,
		private readonly appVersion: AppVersion
	) {}

	public isDevSession() {
		return this.debugEmails.includes(this.isDevLogin?.username) && this.debugIds.includes(this.isDevLogin?.id);
	}

	public showErrors() {
		return (this.debugEmails.includes(this.isDevLogin?.username) && this.debugIds.includes(this.isDevLogin?.id)) || this.useFill;
	}

	public get<T>(key: env): T {
		if (!this.config[key]) return null;
		return this.config[key];
	}

	public fill(key: string, config?: DefaultObject) {
		if (this.config.env === 'prod' && this.debugIds.includes(this.isDevLogin?.id)) {
			const fn = fns[key];
			return fn(config);
		}
		if (this.config.env === 'dev' && this.debugEmails.includes(this.isDevLogin?.username)) {
			const fn = fns[key];
			return fn(config);
		}
		if (this.config.env === 'stage' && this.debugEmails.includes(this.isDevLogin?.username)) {
			const fn = fns[key];
			return fn(config);
		}
		return '';
	}

	public async init() {
		await this.loadConfig();
		return Promise.resolve();
	}

	public async setSentry({ sentryDSN, env }) {
		if (this.isSentryInitialized) return;
		this.isSentryInitialized = true;
		try {
			const version = await this.appVersion.getVersionNumber();
			const build = await this.appVersion.getVersionCode();
			console.log('starting sentry');
			console.log(`Sentry Initialized ${env} ${version}.${build}`);

			if (env !== 'prod') return;

			Sentry.init({
				dsn: sentryDSN,
				replaysSessionSampleRate: 0.1,
				replaysOnErrorSampleRate: 1.0,
				integrations: [new Sentry.Replay()],
				release: `${version}.${build}`,
				environment: env,
				attachStacktrace: true,
				ignoreErrors: ['Non-Error exception captured', 'The object can not be cloned.', '(void 0).info'],
			});
		} catch (e) {
			console.log(`Sentry already initialized ${env}`);
		}
	}
	
	loadConfig() {
		return new Promise(resolve => {
			this.config = environment;
			this.config$.next(this.config);
			resolve([]);
		});
	}
	
	public loadEnv(env) {
		this.config = environment;
		this.config$.next(this.config);
		return Promise.resolve(true);
	}
}
